'use strict';

/**
 * The selected-product contract's receipt words (MAR-411) — what a help selected, or why execution
 * withheld it, in the merchant's words. Requires NOTHING: `decision-receipt` reads it, and the
 * merchant web app bundles `decision-receipt`. The keys are the values of `SELECTION_DROP_REASONS`,
 * `EXCLUSION_REASONS` and `PRODUCT_AVAILABILITY` in `@marva/shared/product-eligibility`, restated as
 * literals so this module stays dependency-free; `product-eligibility.test.js` pins that every value
 * has a phrase.
 *
 * @module @marva/shared/selection-receipt
 */

const DROP_PHRASES = Object.freeze({
  not_a_candidate: 'not one of the products in play',
  excluded: 'excluded',
  unavailable: 'not available',
  unverified: 'availability could not be confirmed',
  unavailable_at_send: 'no longer available at send time',
  availability_unreadable: 'availability could not be re-checked at send time',
  duplicate: 'named twice',
  over_limit: 'over the limit'
});

const EXCLUSION_PHRASES = Object.freeze({
  returned: 'they returned it',
  owned: 'they already own it',
  // MAR-443: a structured negative stance on an attribute the product carries ("never wool").
  rejected: 'they said they don\'t want it',
  contraindicated: 'not right for them',
  shown: 'shown to them recently'
});

const AVAILABILITY_PHRASES = Object.freeze({
  out_of_stock: 'out of stock',
  inactive: 'no longer listed',
  deleted: 'removed from the store',
  unknown: 'stock unconfirmed'
});

/** One dropped product's reason, in the merchant's words. */
function describeDrop(drop) {
  if (!drop) return 'left out';
  if (drop.reason === 'excluded' && EXCLUSION_PHRASES[drop.exclusion]) return EXCLUSION_PHRASES[drop.exclusion];
  if ((drop.reason === 'unavailable' || drop.reason === 'unavailable_at_send') &&
    AVAILABILITY_PHRASES[drop.availability]) {
    return drop.reason === 'unavailable_at_send'
      ? `${AVAILABILITY_PHRASES[drop.availability]} by send time`
      : AVAILABILITY_PHRASES[drop.availability];
  }
  return DROP_PHRASES[drop.reason] || 'left out';
}

const nameOf = (item) => item?.title || `product ${item?.productId}`;

/**
 * The selection clause of a receipt line, from a row's execution evidence (`selectionExecution`)
 * or, before execution, its decision-time selection. Null when the row carries no selection.
 *
 * "selected Linen Shirt; left out Wool Coat (out of stock by send time)"
 * "selected Linen Shirt; left out Coat (…), Tee (…), Scarf (…) and 1 more"
 * "nothing selected could be shown: Wool Coat (out of stock by send time)"
 *
 * @param {object} row - a decision row
 * @returns {string|null}
 */
function describeSelection(row) {
  if (!row || typeof row !== 'object') return null;
  const evidence = row.selectionExecution && typeof row.selectionExecution === 'object' ? row.selectionExecution : null;
  const selected = evidence ? (evidence.selected || []) : (Array.isArray(row.selectedProducts) ? row.selectedProducts : null);
  if (!selected) return null;
  const dropped = evidence ? (evidence.dropped || []) : (row.selectionDropped || []);
  const titles = new Map((row.candidateProducts?.candidates || []).map(c => [c.productId, c.title]));
  const named = (item) => nameOf({ ...item, title: item.title || titles.get(item.productId) || null });
  // Three are named; the rest are counted, never silently cut — the drop that changed the receipt
  // (a compose-stage one rides after execution's) must not vanish from the line.
  const SHOWN = 3;
  const rest = dropped.length - SHOWN;
  const leftOut = dropped.slice(0, SHOWN).map(d => `${named(d)} (${describeDrop(d)})`).join(', ') +
    (rest > 0 ? ` and ${rest} more` : '');
  if (selected.length === 0) {
    return leftOut ? `nothing selected could be shown: ${leftOut}` : 'no product was selected';
  }
  // MAR-411 (B3): a partial drop on a channel that sends the agent's own words withholds — the
  // copy named a product that changed — so the clause says that, not "selected".
  if (evidence && evidence.withheld === 'fixed_copy') {
    return leftOut ? `its message may have named ${leftOut}` : 'its message may have named a product that changed';
  }
  const chosen = `selected ${selected.slice(0, 4).map(named).join(', ')}`;
  return leftOut ? `${chosen}; left out ${leftOut}` : chosen;
}

module.exports = { DROP_PHRASES, EXCLUSION_PHRASES, AVAILABILITY_PHRASES, describeDrop, describeSelection };
